import { IS_PROD, IS_SERVER, env } from "@/env";

// Use GCP Logging format only in server-side production environment
const isProdServer = IS_SERVER && IS_PROD;

// During `next build`, NEXT_PHASE is set to "phase-production-build".
// Suppress INFO/DEBUG logs at build time to keep build output clean.
const isBuildPhase = process.env.NEXT_PHASE === "phase-production-build";

export const logger = {
  info: (message: string, ...args: unknown[]) => {
    if (isBuildPhase) return;
    if (isProdServer) {
      serverLog("INFO", message, null, args);
    } else {
      console.info("INFO", message, ...args);
    }
  },
  warn: (message: string, ...args: unknown[]) => {
    if (isProdServer) {
      serverLog("WARNING", message, null, args);
    } else {
      console.warn("WARNING", message, ...args);
    }
  },
  error: (error: Error | null, message: string, ...args: unknown[]) => {
    if (isProdServer) {
      serverLog("ERROR", message, error, args);
    } else {
      console.error("ERROR", message, error, ...args);
    }
  },
  debug: (message: string, ...args: unknown[]) => {
    if (isBuildPhase) return;
    if (isProdServer) {
      serverLog("DEBUG", message, null, args);
    } else {
      console.debug("DEBUG", message, ...args);
    }
  },
  log: (message: string, ...args: unknown[]) => {
    if (isBuildPhase) return;
    if (isProdServer) {
      serverLog("INFO", message, null, args);
    } else {
      console.info("INFO", message, ...args);
    }
  },
  perf: (message: string, latencyMs: number, ...args: unknown[]) => {
    if (isBuildPhase) return;
    if (isProdServer) {
      serverLog("INFO", message, null, args, latencyMs);
    } else {
      console.info("INFO", message, ...args);
    }
  },
};

function serverLog(
  severity: "INFO" | "WARNING" | "ERROR" | "DEBUG",
  message: string,
  error: Error | null,
  args: unknown[],
  latencyMs: number | undefined = undefined,
) {
  const trace = globalThis.__OTEL_LOG_TRACE_GETTER?.();
  const projectId = env.GOOGLE_CLOUD_PROJECT;

  const log: CloudLoggingRequestLog = {
    severity,
    message,
    timestamp: new Date().toISOString(),
    data: args,
    serviceContext: {
      service: "marketing-website",
      version: env.APP_VERSION || "dev",
    },
  };

  if (trace) {
    // Format trace ID in Google Cloud Logging format: projects/[PROJECT_ID]/traces/[TRACE_ID]
    log["logging.googleapis.com/trace"] = projectId ? `projects/${projectId}/traces/${trace.traceId}` : trace.traceId;
    log["logging.googleapis.com/spanId"] = trace.spanId;
  }

  if (error) {
    log.stack = error.stack;
    log.exception = error.name;
  }

  if (latencyMs) {
    log.httpRequest = {
      latency: `${Math.ceil(latencyMs)}ms`,
    };
  }

  console.log(JSON.stringify(log));
}

// https://docs.cloud.google.com/logging/docs/structured-logging#structured_logging_special_fields
export interface CloudLoggingRequestLog {
  // Standard properties
  severity: "INFO" | "WARNING" | "ERROR" | "DEBUG";
  message: string;
  httpRequest?: {
    requestMethod?: string;
    requestUrl?: string;
    status?: number;
    latency?: string;
    protocol?: string;
  };

  "logging.googleapis.com/trace"?: string;
  "logging.googleapis.com/spanId"?: string;
  "logging.googleapis.com/labels"?: Record<string, string>;

  // Error properties
  stack?: string;
  exception?: string;

  // Custom properties
  labels?: {
    // "2xx", "4xx", "5xx"
    status_class: string;
    request_type: "api" | "page";
    route_pattern: string;
    bot?: string; // e.g., "Googlebot", "GPTBot"
  };

  serviceContext?: {
    service: string;
    version: string;
  };

  route?: string; // Next.js route pattern
  duration_ms?: number; // Numeric for aggregation
  timestamp: string; // ISO 8601 timestamp

  data?: unknown; // This will end up in the jsonPayload field
}
